import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { Image as ImageIcon, Check, UploadCloud, Image as ImagePlaceholder, Loader2 } from "lucide-react";
import { Input } from "@shared/ui/input";
import { Textarea } from "@shared/ui/textarea";
import { Switch } from "@shared/ui/switch";
import { AuditTrailModal } from "./AuditTrailModal";
import GuestPortalPreview from "./GuestPortalPreview";
import { useUpdateGuestPortalBranding, useGetGuestPortalBranding } from "../../hooks";

const GuestPortalBranding = ({ remoteUtilityId }: any) => {
  const [isEditing, setIsEditing] = useState(true);
  const { mutate: updateGuestPortalBranding, isPending } = useUpdateGuestPortalBranding();
  const { data: brandingResponse, refetch: refetchBranding } = useGetGuestPortalBranding({ remote_utility_id: remoteUtilityId });
  
  // Left Panel Background
  const [bgMode, setBgMode] = useState<'gradient' | 'photo'>('gradient');
  const [startColor, setStartColor] = useState('#000000');
  const [endColor, setEndColor] = useState('#1a3a6f');
  const [gradientDir, setGradientDir] = useState('to top right');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  
  // Left Panel Text
  const [headline, setHeadline] = useState('Manage your utility account');
  const [supportingText, setSupportingText] = useState('Sign in or create an account to access bills, consumption history, and more.');
  
  // Announcement Banner
  const [showBanner, setShowBanner] = useState(true);
  const [bannerMessage, setBannerMessage] = useState('Need help? Call us at (303) 555-0100 Mon–Fri, 8 AM–5 PM');
  const [bannerBg, setBannerBg] = useState('#111827');
  const [bannerText, setBannerText] = useState('#ffffff');

  useEffect(() => {
    const data = brandingResponse?.result || brandingResponse;
    if (data) {
      if (data.leftPanelBackground) {
        setBgMode(data.leftPanelBackground.type || 'gradient');
        if (data.leftPanelBackground.startColour) setStartColor(`#${data.leftPanelBackground.startColour}`);
        if (data.leftPanelBackground.endColour) setEndColor(`#${data.leftPanelBackground.endColour}`);
        if (data.leftPanelBackground.gradientDirection) setGradientDir(data.leftPanelBackground.gradientDirection);
      }
      if (data.leftPanelText) {
        if (data.leftPanelText.headline) setHeadline(data.leftPanelText.headline);
        if (data.leftPanelText.supportingText) setSupportingText(data.leftPanelText.supportingText);
      }
      if (data.announcementBanner) {
        setShowBanner(data.announcementBanner.announcementBanner ?? true);
        if (data.announcementBanner.bannerMessage) setBannerMessage(data.announcementBanner.bannerMessage);
        if (data.announcementBanner.bgColour) setBannerBg(`#${data.announcementBanner.bgColour}`);
        if (data.announcementBanner.textColour) setBannerText(`#${data.announcementBanner.textColour}`);
      }
    }
  }, [brandingResponse]);

  const handleReset = () => {
    setBgMode('gradient');
    setStartColor('#000000');
    setEndColor('#1a3a6f');
    setGradientDir('to top right');
    setPhotoFile(null);
    setHeadline('Manage your utility account');
    setSupportingText('Sign in or create an account to access bills, consumption history, and more.');
    setShowBanner(true);
    setBannerMessage('Need help? Call us at (303) 555-0100 Mon–Fri, 8 AM–5 PM');
    setBannerBg('#111827');
    setBannerText('#ffffff');
  };

  const handleSave = () => {
    const formData = new FormData();
    formData.append('remote_utility_id', remoteUtilityId.toString());
    
    const leftPanelBg = {
      type: bgMode,
      ...(bgMode === 'gradient' ? {
        start_colour: startColor.replace('#', ''),
        end_colour: endColor.replace('#', ''),
        gradient_direction: gradientDir,
      } : {})
    };
    formData.append('left_panel_background', JSON.stringify(leftPanelBg));

    const leftPanelTxt = {
      headline: headline,
      supporting_text: supportingText
    };
    formData.append('left_panel_text', JSON.stringify(leftPanelTxt));

    const announcementBanner = {
      announcement_banner: showBanner,
      banner_message: bannerMessage,
      bg_colour: bannerBg.replace('#', ''),
      text_colour: bannerText.replace('#', '')
    };
    formData.append('announcement_banner', JSON.stringify(announcementBanner));

    if (bgMode === 'photo' && photoFile) {
      formData.append('left_panel_image', photoFile);
    }

    updateGuestPortalBranding(formData, {
      onSuccess: () => refetchBranding()
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
      {/* Settings Column */}
      <Card className="shadow-sm border-gray-200">
        <CardContent className="p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Guest Portal Branding</h2>
          <p className="text-sm text-gray-500 mb-6">
            Configure the left hero panel and banner text shown on the public guest portal — visible to users who are not logged in.
          </p>

          <div className={!isEditing ? 'pointer-events-none opacity-60' : ''}>
            
            {/* LEFT PANEL BACKGROUND */}
            <div className="mb-8">
              <h3 className="text-xs font-bold text-gray-500  tracking-widest mb-3">Left Panel Background</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div 
                  className={`border rounded-lg p-4 cursor-pointer flex items-center gap-3 transition-colors ${bgMode === 'gradient' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                  onClick={() => setBgMode('gradient')}
                >
                  <div className={`w-8 h-8 rounded bg-gradient-to-tr from-black to-blue-800`}></div>
                  <div className="flex-1">
                    <div className="text-sm font-bold text-gray-900">Gradient</div>
                    <div className="text-[10px] text-gray-500">Set start and end colours for a gradient background.</div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${bgMode === 'gradient' ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                    {bgMode === 'gradient' && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>

                <div 
                  className={`border rounded-lg p-4 cursor-pointer flex items-center gap-3 transition-colors ${bgMode === 'photo' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                  onClick={() => setBgMode('photo')}
                >
                  <div className={`p-1.5 rounded bg-gray-100 text-gray-500`}>
                    <ImageIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-bold text-gray-900">Photo</div>
                    <div className="text-[10px] text-gray-500">Upload a PNG, JPG, or WebP (up to 3 MB).</div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${bgMode === 'photo' ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                    {bgMode === 'photo' && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
              </div>

              {bgMode === 'gradient' && (
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Start Colour</label>
                      <div className="flex items-center border border-gray-300 rounded-md p-1">
                        <input type="color" value={startColor} onChange={e => setStartColor(e.target.value)} className="w-8 h-8 rounded border-0 p-0 cursor-pointer" />
                        <Input value={startColor} onChange={e => setStartColor(e.target.value)} className="border-0 focus-visible:ring-0 h-8 font-mono text-sm " />
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-gray-700 mb-1">End Colour</label>
                      <div className="flex items-center border border-gray-300 rounded-md p-1">
                        <input type="color" value={endColor} onChange={e => setEndColor(e.target.value)} className="w-8 h-8 rounded border-0 p-0 cursor-pointer" />
                        <Input value={endColor} onChange={e => setEndColor(e.target.value)} className="border-0 focus-visible:ring-0 h-8 font-mono text-sm " />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Gradient Direction</label>
                    <select 
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      value={gradientDir}
                      onChange={e => setGradientDir(e.target.value)}
                    >
                      <option value="to top right">Diagonal (bottom-left → top-right)</option>
                      <option value="to bottom right">Diagonal (top-left → bottom-right)</option>
                      <option value="to right">Horizontal (left → right)</option>
                      <option value="to bottom">Vertical (top → bottom)</option>
                    </select>
                  </div>
                </div>
              )}

              {bgMode === 'photo' && !photoFile && (
                <label className="border border-dashed border-gray-300 rounded-lg p-8 flex flex-col items-center justify-center bg-gray-50/50 transition-all hover:bg-gray-50 cursor-pointer text-center w-full block">
                  <input type="file" className="hidden" accept="image/png, image/jpeg, image/webp" onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; if (!["image/png","image/jpeg","image/webp"].includes(f.type)) { e.target.value = ""; return; } setPhotoFile(f); }} />
                  <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center mb-3 mx-auto">
                    <UploadCloud className="w-5 h-5 text-blue-600" />
                  </div>
                  <p className="text-sm font-bold text-gray-900 mb-1">Click to upload photo</p>
                  <p className="text-xs text-gray-500">PNG, JPG, or WebP up to 3 MB</p>
                </label>
              )}

              {bgMode === 'photo' && photoFile && (
                <div className="border border-dashed border-gray-300 rounded-lg p-8 flex flex-col items-center justify-center bg-gray-50/50 w-full">
                  <div className="w-full flex items-center justify-between">
                    <div className="flex items-center gap-3">
                       <div className="p-3 bg-blue-50 rounded-lg text-blue-600">
                         <ImagePlaceholder className="w-6 h-6" />
                       </div>
                       <div>
                         <p className="text-sm font-bold text-gray-900 truncate max-w-[200px]">{photoFile.name}</p>
                         <p className="text-xs text-gray-500">{(photoFile.size / 1024).toFixed(1)} KB</p>
                       </div>
                    </div>
                    <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50 text-sm" onClick={() => setPhotoFile(null)}>
                      Remove
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* LEFT PANEL TEXT */}
            <div className="mb-8">
              <h3 className="text-xs font-bold text-gray-500  tracking-widest mb-3">Left Panel Text</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Headline <span className="text-red-500">*</span></label>
                  <Input value={headline} onChange={e => setHeadline(e.target.value)} maxLength={60} />
                  <div className="flex justify-between mt-1">
                    <p className="text-[10px] text-gray-400">Large headline shown on the left hero panel.</p>
                    <p className="text-[10px] text-gray-400">{headline.length} / 60</p>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Supporting Text</label>
                  <Textarea value={supportingText} onChange={e => setSupportingText(e.target.value)} maxLength={140} rows={2} className="resize-none" />
                  <div className="flex justify-end mt-1">
                    <p className="text-[10px] text-gray-400">{supportingText.length} / 140</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ANNOUNCEMENT BANNER */}
            <div>
              <h3 className="text-xs font-bold text-gray-500  tracking-widest mb-3">Announcement Banner</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700">Show Announcement Banner</label>
                    <p className="text-xs text-gray-500">Display a help / info strip below the top navigation bar.</p>
                  </div>
                  <Switch checked={showBanner} onCheckedChange={setShowBanner} />
                </div>
                
                {showBanner && (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Banner Message <span className="text-red-500">*</span></label>
                      <Input value={bannerMessage} onChange={e => setBannerMessage(e.target.value)} maxLength={120} />
                      <div className="flex justify-between mt-1">
                        <p className="text-[10px] text-gray-400">Shown in a slim strip directly below the navigation bar.</p>
                        <p className="text-[10px] text-gray-400">{bannerMessage.length} / 120</p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Background Colour</label>
                        <div className="flex items-center border border-gray-300 rounded-md p-1">
                          <input type="color" value={bannerBg} onChange={e => setBannerBg(e.target.value)} className="w-8 h-8 rounded border-0 p-0 cursor-pointer" />
                          <Input value={bannerBg} onChange={e => setBannerBg(e.target.value)} className="border-0 focus-visible:ring-0 h-8 font-mono text-sm " />
                        </div>
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Text Colour</label>
                        <div className="flex items-center border border-gray-300 rounded-md p-1">
                          <input type="color" value={bannerText} onChange={e => setBannerText(e.target.value)} className="w-8 h-8 rounded border-0 p-0 cursor-pointer" />
                          <Input value={bannerText} onChange={e => setBannerText(e.target.value)} className="border-0 focus-visible:ring-0 h-8 font-mono text-sm " />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-100 mt-6">
            <AuditTrailModal 
              title="Guest Portal Branding" 
              remoteUtilityId={remoteUtilityId} 
              actionType="GUEST_BRANDING_UPDATE" 
            />

            <Button variant="outline" className="text-gray-600 gap-2" onClick={handleReset} disabled={isPending}>
              Reset
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={handleSave} disabled={isPending || !isEditing}>
              {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {isPending ? "Saving..." : "Save Guest Branding"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <GuestPortalPreview 
        bgMode={bgMode}
        startColor={startColor}
        endColor={endColor}
        gradientDir={gradientDir}
        photoFile={photoFile}
        headline={headline}
        supportingText={supportingText}
        showBanner={showBanner}
        bannerMessage={bannerMessage}
        bannerBg={bannerBg}
        bannerText={bannerText}
      />
    </div>
  );
};

export default GuestPortalBranding;
